import {
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { TriangleAlert, X } from "lucide-react";
import {
  useEffect,
  useState,
  type ChangeEvent,
  type KeyboardEvent
} from "react";
import { useTranslation } from "react-i18next";
import {
  AuthorTypography,
  CommentAuthorAvatar,
  CommentInputWrapper,
  CommentOuterStack,
  CommentTypography,
  LoadMoreButton,
  NameTypography
} from "./Comment.style";
import type { CommentProps } from "./Comment.types";
import { handleChevron } from "./utils";
import {
  Icon,
  Modal,
  Spinner,
  TopicCardSignature
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import {
  CommentInputBase,
  ForbiddenLabelTypography
} from "../../components/CommentsSection/CommentsSection.style";
import {
  useCommentDeleteQuery,
  useCommentPutQuery,
  useInfiniteReplyQuery,
  useReplyPostQuery
} from "../../queries/community";
import { useAuthStore } from "../../stores";
import { BadgeBox } from "../Commons/BadgeBox";

export function Comment({
  authorName,
  changed,
  commentBody,
  commentOwner,
  created,
  discussionId,
  id,
  image,
  isReply,
  parentCommentId,
  replies,
  selectedFilter,
  selectedTopic,
  topicOwnerId

} : CommentProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const userId = useAuthStore(state => state.userData?.id_user);

  const [showInput, setShowInput] = useState<boolean>(false);
  const [showReplyComment, setShowReplyComment] = useState<boolean>(false);
  const [showRemove, setShowRemove] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState<"reply" | "modify">("reply");
  const [inputContent, setInputContent] = useState<string>("");
  const [showReplies, setShowReplies] = useState<boolean>(false);
  const [showForbiddenlabel, setShowForbiddenlabel] = useState(false);

  const isModified = created !== changed;

  const {
    data: results,
    fetchNextPage,
    hasNextPage,
    // isFetchingNextPage,
    // isFetching,
    isLoading
    // refetch: refetchReplies
  } = useInfiniteReplyQuery({ commentId: id, enabled: showReplies });

  const {
    mutate: mutateReply,
    isPending: replyIsLoading,
    isIdle: replyIsIdle,
    isError: replyIsError,
    error: replyError
  } = useReplyPostQuery();

  const {
    mutate: mutateModifyComment,
    isPending: modifyIsLoading,
    isIdle: modifyIsIdle,
    isError: modifyIsError,
    error: modifyError
  } = useCommentPutQuery();

  const {
    mutate: mutateDeleteComment,
    isPending: deleteIsLoading,
    isIdle: deleteIsIdle,
    isError: deleteIsError
  } = useCommentDeleteQuery({
    commentId: id,
    discussionId: discussionId,
    parentCommentId: parentCommentId,
    selectedFilter:selectedFilter,
    selectedTopic:selectedTopic
  });

  //managing fobidden word for reply + showing replies + hide input
  useEffect(() => {
    if(!replyIsIdle && !replyIsLoading) {
      if(replyIsError) {
        const errorObject = JSON.parse(JSON.parse(replyError as unknown as string).errors.FATAL[0].message);
        if(errorObject.message == "Forbidden word has been detected.") {
          setShowForbiddenlabel(true);
        }
        setShowReplyComment(false);
      } else {
        setShowReplyComment(false);
        setShowInput(false);
        setShowReplies(true);
      }
    }
  },[replyError, replyIsError, replyIsIdle, replyIsLoading]);

  //managing comment/reply deletion
  useEffect(() => {
    if(!deleteIsIdle && !deleteIsLoading) {
      if(deleteIsError) {
        console.log("comment deletion error");
      }
      setShowInput(false);
    }
  },[deleteIsError, deleteIsIdle, deleteIsLoading]);

  //managing fobidden word for modify
  useEffect(() => {
    if(!modifyIsIdle && !modifyIsLoading) {
      if(modifyIsError) {
        const errorObject = JSON.parse(JSON.parse(modifyError as unknown as string).errors.FATAL[0].message);
        if(errorObject.message == "Forbidden word has been detected.") {
          setShowForbiddenlabel(true);
        }
      } else {
        setShowInput(false);
      }
    }

  },[modifyError, modifyIsError, modifyIsIdle, modifyIsLoading]);

  const handleSubmitInput = () => {
    if (inputContent.length === 0) {
      return;
    }
    if (selectedAction == "modify") {
      if (modifyIsLoading) {
        return;
      }
      mutateModifyComment({
        body: inputContent,
        commentId: id,
        discussionId: discussionId,
        parentCommentId: parentCommentId
      });
    } else {
      if (replyIsLoading) {
        return;
      }
      mutateReply({ body: inputContent, commentId: id, discussionId: discussionId });
    }
  };

  return (
    <>
      { /* { deleteIsLoading || replyIsLoading || modifyIsLoading ? <Spinner /> : //TODO settare dei loader ai mutate */ }
      <Stack direction="row" gap={ "16px" }>
        <CommentOuterStack direction="row" gap={ "16px" }>
          <Stack aria-hidden={ true } tabIndex={ -1 } gap={ "1rem" }>
            <CommentAuthorAvatar alt={ authorName } src={ image } >
              {
                authorName.length > 1
                  ? `${authorName.split(" ")?.[0]?.[0]}${authorName.split(" ")?.[1]?.[0]}`
                  : authorName.length > 0
                    ? authorName.split(" ")?.[0]?.[0]
                    : ""
              }
            </CommentAuthorAvatar>
          </Stack>
          <Stack flexGrow={ 1 }>
            <Stack
              gap={ "8px" }
              position={ "relative" }
              bottom={ "2px" }
              sx={ {
                "&:focus-visible": {
                  outline: `2px solid ${theme.customColors.headerIcon}`,
                  outlineOffset: "2px"
                }
              } } tabIndex={ 0 } aria-label={ t("community_answare_post_aria_label",{ subject:authorName,answare:commentBody,time:getcreatedLabel(created) }) } >
              <Stack aria-hidden={ true } tabIndex={ -1 } direction={ "row" } gap={ "0.75rem" } alignItems={ "center" } flexWrap={ "wrap" }>
                <NameTypography>
                  { authorName }
                </NameTypography>
                { (commentOwner === topicOwnerId ) &&
                  <BadgeBox
                    background={ theme.customColors.backgroundSecondary }
                    textColor={ "inherit" }
                    border={ `1px solid ${theme.customColors.textTag}` }
                  >
                    <AuthorTypography>
                      { t("author") }
                    </AuthorTypography>
                  </BadgeBox>
                }
                <TopicCardSignature
                  color={ theme.customColors.exploreCardText }
                  created={ getcreatedLabel(created) }
                  isModified={ isModified }
                  size={ "small" }
                  hideIcon
                />
              </Stack>
              <CommentTypography aria-hidden={ true } tabIndex={ -1 }>{ commentBody }</CommentTypography>
            </Stack>
            { showInput ?
              <Stack>
                { showForbiddenlabel &&
                  <ForbiddenLabelTypography
                    tabIndex={ 0 }
                    key={ "forbidden-label-reply" }
                    sx={ {
                      position: "relative",
                      top: "8px"
                    } }
                  >
                    { t("forbidden_label_comment") }
                  </ForbiddenLabelTypography>
                }
                <Stack
                  direction={ "row" }
                  gap={ "16px" }
                  paddingTop={ "8px" }
                >
                  <CommentInputWrapper>
                    <CommentInputBase
                      defaultValue={ !showReplyComment ? commentBody : "" }
                      onChange={ (changeEvent: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setShowForbiddenlabel(false);
                        setInputContent(changeEvent.target.value);
                      } }
                      onKeyUp={ (keyboardEvent: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        if (keyboardEvent.code === "Enter") {
                          handleSubmitInput();
                        }
                      } }
                      placeholder={
                        selectedAction == "modify" ?
                          t("enter_change") :
                          t("enter_answer")
                      }
                      endAdornment={
                        <X
                          aria-label={ t("close") }
                          role="button"
                          tabIndex={ 0 }
                          size="1.5rem"
                          color={ theme.customColors.systemSecondary01 }
                          style={ {
                            cursor: "pointer",
                            position: "absolute",
                            right: "1rem"
                          } }
                          onClick={ () => {
                            setShowForbiddenlabel(false);
                            setShowInput(false);
                          } }
                        />
                      }
                    />
                  </CommentInputWrapper>
                  <ButtonDigited
                    typology="primary"
                    aria-label={ t("send") }
                    disabled={ inputContent.length == 0 || replyIsLoading || modifyIsLoading }
                    onClick={ () => {
                      handleSubmitInput();
                    } }
                  >
                    { t("send") }
                  </ButtonDigited>
                </Stack>
              </Stack> :
              showRemove ?
                <Stack
                  direction={ "row" }
                  gap={ "12px" }
                  alignItems={ "center" }
                  flexWrap={ "wrap" }
                  marginTop={ "0.5rem" }
                  sx={ {
                    border: `1px solid ${theme.customColors.textWarning}`,
                    borderRadius: "0.5rem",
                    background: theme.customColors.backgroundPrimaryControl,
                    width: "fit-content",
                    padding: "0.5rem 0.75rem"
                  } }
                >
                  <TriangleAlert
                    size={ "1rem" }
                    color={ theme.customColors.textWarning }
                  />
                  <Typography
                    sx={ {
                      color: theme.customColors.textPrimary,
                      fontSize: "0.875rem"
                    } }
                  >
                    { t("comment_question_delete") }
                  </Typography>
                  <Stack direction={ "row" } alignItems={ "center" }>
                    <ButtonDigited
                      typology="tertiary"
                      sx={ {
                        fontSize: "0.75rem"
                      } }
                      onClick={ () => {
                        mutateDeleteComment();
                      } }
                    >
                      { t("delete") }
                    </ButtonDigited>
                    <ButtonDigited
                      typology="tertiary"
                      sx={ {
                        fontSize: "0.75rem"
                      } }
                      onClick={ () => setShowRemove(false) }
                    >
                      { t("annul") }
                    </ButtonDigited>
                  </Stack>
                </Stack> :
                <Stack direction={ "row" } gap={ "0.5rem" }>
                  { !isReply &&
                    <ButtonDigited
                      aria-label={ t("reply_to",{ name:authorName }) }
                      typology="tertiary"
                      size="small"
                      sx={ {
                        paddingLeft: 0,
                        fontSize: "0.75rem"
                      } }
                      onClick={ () => {
                        setShowInput(true);
                        setShowReplyComment(true);
                        setInputContent("");
                        setSelectedAction("reply");
                      } }>
                      { t("reply") }
                    </ButtonDigited>
                  }
                  { userId && commentOwner == userId.toString() &&
                    <>
                      <ButtonDigited
                        typology="tertiary"
                        size="small"
                        sx={ {
                          fontSize: "0.75rem"
                        } }
                        onClick={ () => {
                          setShowInput(true);
                          setInputContent(commentBody);
                          setSelectedAction("modify");
                        } }>
                        { t("modify") }
                      </ButtonDigited>

                      <ButtonDigited
                        typology="tertiary"
                        size="small"
                        sx={ {
                          fontSize: "0.75rem"
                        } }
                        onClick={ () => {
                          setShowRemove(true);
                        } }>
                        { t("remove") }
                      </ButtonDigited>
                    </>
                  }
                </Stack>
            }
            <Modal
              open={ showRemove && !deleteIsIdle && !deleteIsLoading && deleteIsError }
              onClose={ ()=> { setShowRemove(false) } }
              description={ "An error occurred while deleting comment" }
              title={ "Comment Deletion Error" }
            />
            { !isReply && Number(replies) > 0 &&
              <ButtonDigited
                typology="tertiary"
                onClick={ () => setShowReplies(!showReplies) }
                sx={ {
                  padding: 0,
                  gap: "0.5rem",
                  textDecoration: "none !important",
                  width: "fit-content",
                  fontSize: "12px"
                } }
              >
                <>
                  { handleChevron(showReplies) }
                  { showReplies
                    ? t("hide_replies", { count: Number(replies) })
                    : t("show_replies", { count: Number(replies) }) }
                </>
              </ButtonDigited>
            }
            { showReplies &&
              <Stack>
                { !isLoading && results &&
                results?.pages?.map((page) => (
                  page?.data.map((comment) => {

                    const ownerUserId = comment.owner_id;
                    const ownerUser = page?.enrichedInformation?.users?.find(user => user.owner_id === ownerUserId);

                    return <Comment
                      selectedFilter={ null }
                      selectedTopic={ null }
                      key={ `comment-${comment.comment_id}` }
                      authorName={ `${ownerUser?.name} ${ownerUser?.surname}` || "" }
                      commentBody={ comment.body }
                      commentOwner={ comment.owner_id }
                      created={ comment.created }
                      id = { comment.comment_id }
                      image={ ownerUser?.profileImageUrl || "" }
                      isReply={ true }
                      replies={ comment.reply }
                      discussionId={ discussionId }
                      parentCommentId={ id }
                      changed={ comment.changed }
                      topicOwnerId={ topicOwnerId }

                    />;
                  }
                  )
                ))
                }
                { isLoading &&
                  <Spinner
                    size={ 24 }
                    thickness={ 3 }
                    padding={ 0 }
                    sx={ { color:theme.customColors.accentVariantA } }
                  />
                }
                { hasNextPage &&
                  <LoadMoreButton onClick={ () => fetchNextPage() }>
                    { t("load_more_replies") }
                    <Icon
                      color={ theme.customColors.systemPrimary02 }
                      icon="dropdown"
                      size={ 24 }
                    />
                  </LoadMoreButton>
                }
              </Stack>
            }
          </Stack>
        </CommentOuterStack>
      </Stack>
    </>
  );

  // duplicated in TopicCard Component
  function getcreatedLabel(created: string) {
    if(created) {
      const date = new Date(created);
      const now = new Date();
      const hours = Math.trunc(((now.getTime() - date.getTime()) / 1000 / 3600));
      if (hours < 1) return t("now");
      if (hours < 24) return `${t("hour", { count: hours })} ${t("ago")}`;
      if (hours < 48) return `${t("day", { count: 1 })} ${t("ago")}`;
      if (hours < 168) return `${t("day", { count: Math.floor(hours / 24) })} ${t("ago")}`;
      if (hours < 336) return `${t("week", { count: 1 })} ${t("ago")}`;
      else return format(date, "dd/MM/yyy");
    } else {
      return "undefined";
    }
  }
}
